import { Plus, Trash2 } from 'lucide-react';
import { Fragment } from 'react';
import { Controller, useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ParameterDropdown } from '@/Domain/components/Action/Lexical/component/ParameterDropdown';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Field, FieldGroup, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { MetadataConditionConnector } from '@/Domain/components/Shared/MetadataCondition/MetadataConditionConnector';
import { MetadataConditionOperator } from '@/Domain/components/Shared/MetadataCondition/MetadataConditionOperator';
import { MetadataConditionValue } from '@/Domain/components/Shared/MetadataCondition/MetadataConditionValue';
import { changeMetadataConditionOperator } from '@/Domain/helper/metadataRunCondition.helper';
import {
  ACTION_LIMITS,
  HttpActionForm,
} from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

type Props = { form: UseFormReturn<HttpActionForm> };

export const RunConditions = ({ form }: Props) => {
  const { t } = useTranslation('actions_page', { keyPrefix: 'sheet.form.run_conditions' });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'run_conditions',
  });
  const conditions = useWatch({ control: form.control, name: 'run_conditions' }) ?? [];
  const atLimit = fields.length >= ACTION_LIMITS.MEMORY_CAPTURE_COUNT;
  const handleAdd = () =>
    append({ id: uuidv4(), metadata_key: '', operator: 'is_truthy', value: null, values: [] });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <IconButton
          size="sm"
          onClick={handleAdd}
          disabled={atLimit}
          aria-label={t('add_button')}
          data-testid="run-condition-add-button"
        >
          <Plus />
        </IconButton>
      </div>
      {fields.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-4">
          <p className="text-sm font-medium text-foreground">{t('empty.title')}</p>
          <p className="max-w-md text-center text-sm text-muted-foreground">
            {t('empty.description')}
          </p>
          <Button onClick={handleAdd} data-testid="run-condition-add-empty-button">
            <Plus data-icon="inline-start" />
            {t('add_button')}
          </Button>
        </div>
      ) : (
        fields.map((field, index) => {
          const condition = conditions[index] ?? field;
          const valueError = form.formState.errors.run_conditions?.[index]?.value?.message;
          const valuesError = form.formState.errors.run_conditions?.[index]?.values;
          const error =
            valueError ??
            valuesError?.message ??
            (Array.isArray(valuesError)
              ? valuesError.find((item) => item?.message)?.message
              : undefined);
          const revalidateOperand = () =>
            void form.trigger([`run_conditions.${index}.value`, `run_conditions.${index}.values`]);
          return (
            <Fragment key={field.id}>
              {index > 0 && <MetadataConditionConnector />}
              <FieldGroup className="gap-3 rounded-xl border border-border bg-muted p-3">
                <div className="flex items-start gap-2">
                  <FormField
                    name={`run_conditions.${index}.metadata_key`}
                    control={form.control}
                    render={({ field: inputField, fieldState }) => (
                      <FormItem className="min-w-0 flex-1">
                        <FieldLabel htmlFor={`run-condition-key-${index}`} className="sr-only">
                          {t('table.metadata_key')}
                        </FieldLabel>
                        <FormControl>
                          <div className="flex items-center gap-1">
                            <Input
                              {...inputField}
                              id={`run-condition-key-${index}`}
                              data-testid={`run-condition-key-${index}`}
                              size="sm"
                              maxLength={100}
                              placeholder={t('key_placeholder')}
                              error={!!fieldState.error}
                              aria-invalid={!!fieldState.error}
                              className="min-w-0 flex-1"
                            />
                            <ParameterDropdown
                              onlyMetadataKeys
                              parameters={[]}
                              onSelect={(option) =>
                                inputField.onChange(option.name.replace(/^metadata_/, ''))
                              }
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <IconButton
                    size="sm"
                    variant="secondary"
                    onClick={() => remove(index)}
                    data-testid={`run-condition-remove-${index}`}
                    aria-label={t('remove_aria')}
                  >
                    <Trash2 />
                  </IconButton>
                </div>
                <Field>
                  <FieldLabel htmlFor={`run-condition-operator-${index}`} className="sr-only">
                    {t('table.condition')}
                  </FieldLabel>
                  <Controller
                    control={form.control}
                    name={`run_conditions.${index}.operator`}
                    render={({ field: ctrl }) => (
                      <MetadataConditionOperator
                        id={`run-condition-operator-${index}`}
                        value={ctrl.value}
                        onChange={(operator) => {
                          const next = changeMetadataConditionOperator(
                            { ...condition, value: condition.value ?? null },
                            operator,
                          );
                          ctrl.onChange(next.operator);
                          form.setValue(`run_conditions.${index}.value`, next.value, {
                            shouldDirty: true,
                          });
                          form.setValue(`run_conditions.${index}.values`, next.values, {
                            shouldDirty: true,
                          });
                          revalidateOperand();
                        }}
                      />
                    )}
                  />
                </Field>
                <Controller
                  control={form.control}
                  name={`run_conditions.${index}.values`}
                  render={({ field: valuesField }) => (
                    <MetadataConditionValue
                      id={`run-condition-value-${index}`}
                      operator={condition.operator}
                      value={condition.value ?? null}
                      values={valuesField.value}
                      error={error}
                      onChange={(operand) => {
                        form.setValue(`run_conditions.${index}.value`, operand.value, {
                          shouldDirty: true,
                        });
                        valuesField.onChange(operand.values);
                        revalidateOperand();
                      }}
                    />
                  )}
                />
              </FieldGroup>
            </Fragment>
          );
        })
      )}
    </div>
  );
};
